import React, { memo, useMemo, useState, useCallback } from "react";

import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

import { COVERS_HALFCARD_CONSTANTS, COVERS_MIN, COVERS_MAX, COVERS_MAX_DIGITS } from "./constant";
import {
    Container,
    IllustrationContainer,
    IconWrapper,
    TextContainer,
    InputWrapper,
    CTAWrapper,
} from "./styled";
import { ICoversHalfCardProps } from "./types";

const CoversHalfCard = ({ show, onClose, onSubmit, isAndroid = false }: ICoversHalfCardProps) => {
    const [coversInput, setCoversInput] = useState("");

    const handleChange = useCallback((val: string) => {
        // strip non-digits and clamp to the max number of digits. The DOM-level
        // `maxLength` below is the primary guard; this stays as a defense
        // in depth in case the DOM-level cap is bypassed (paste, autofill etc.).
        setCoversInput(val.replace(/\D/g, "").slice(0, COVERS_MAX_DIGITS));
    }, []);

    // Hard-block non-digit input at the DOM level. `inputMode="numeric"` and
    // `pattern="[0-9]*"` only constrain mobile keypads / form validation, so
    // desktop browsers (e.g. on staging) can still type alphabets. This
    // handler fires before the character is inserted and lets us reject it.
    const handleBeforeInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        const data = (e.nativeEvent as InputEvent).data;
        if (data && /\D/.test(data)) {
            e.preventDefault();
        }
    }, []);

    const handleSubmit = useCallback(() => {
        onSubmit(Number(coversInput));
        setCoversInput("");
    }, [coversInput, onSubmit]);

    const handleClose = useCallback(() => {
        setCoversInput("");
        onClose();
    }, [onClose]);

    const coversValue = Number(coversInput);
    const isCtaDisabled = !coversInput || coversValue < COVERS_MIN || coversValue > COVERS_MAX;

    const textFieldDetails = useMemo(
        () => ({
            type: TextFieldTypes.TEXT,
            name: "covers",
            label: "",
            value: coversInput,
            onChange: handleChange,
            placeHolderStyle: { display: "none" },
            attrs: {
                inputMode: "numeric" as const,
                pattern: "[0-9]*",
                maxLength: COVERS_MAX_DIGITS,
                placeholder: COVERS_HALFCARD_CONSTANTS.INPUT_LABEL,
                onBeforeInput: handleBeforeInput,
            },
        }),
        [coversInput, handleChange, handleBeforeInput]
    );

    return (
        <HalfCard
            isOpen={show}
            onClose={handleClose}
            closeOnOverlayClick={true}
            hideCloseIcon={true}
            backgroundColor="background_Secondary"
        >
            <Container>
                <IllustrationContainer>
                    <IconWrapper>
                        <Image
                            cloudinaryId={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_ID}
                            alt={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_ALT}
                            cloudinaryHeight={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE * 2}
                            cloudinaryWidth={COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE * 2}
                            height={String(COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE)}
                            width={String(COVERS_HALFCARD_CONSTANTS.ILLUSTRATION_SIZE)}
                        />
                    </IconWrapper>
                </IllustrationContainer>

                <TextContainer>
                    <Text variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                        {COVERS_HALFCARD_CONSTANTS.TITLE}
                    </Text>
                    <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                        {COVERS_HALFCARD_CONSTANTS.DESCRIPTION}
                    </Text>
                </TextContainer>

                <InputWrapper {...(isAndroid && { "data-keyboard-scroll": "true" })}>
                    <TextField textFieldDetails={textFieldDetails} />
                </InputWrapper>

                <CTAWrapper>
                    <SuperButton
                        buttonType="primary"
                        buttonSize="large"
                        onClick={handleSubmit}
                        isDisabled={isCtaDisabled}
                    >
                        {COVERS_HALFCARD_CONSTANTS.CTA_TEXT}
                    </SuperButton>
                </CTAWrapper>
            </Container>
        </HalfCard>
    );
};

export default memo(CoversHalfCard);
